import * as React from 'react';
import Avatar from 'antd/lib/avatar';
import { AICodeAuthorProvider } from '@/types';
import { getAvatarColorForName } from '../styles/globalStyles';
import claudeCodeAvatar from '../../../assets/ai-avatar-claude-code.svg';
import cursorAvatar from '../../../assets/ai-avatar-cursor.svg';
import githubCopilotAvatar from '../../../assets/ai-avatar-github-copilot.svg';

const providerDisplayNames: Record<AICodeAuthorProvider, string> = {
    [AICodeAuthorProvider.Cursor]: 'Cursor',
    [AICodeAuthorProvider.Claude]: 'Claude',
    [AICodeAuthorProvider.Copilot]: 'Copilot',
};

const providerAvatarSrc: Record<AICodeAuthorProvider, string> = {
    [AICodeAuthorProvider.Claude]: claudeCodeAvatar,
    [AICodeAuthorProvider.Cursor]: cursorAvatar,
    [AICodeAuthorProvider.Copilot]: githubCopilotAvatar,
};

export const displayNameForAICodeAuthorProvider = (provider: AICodeAuthorProvider): string =>
    providerDisplayNames[provider] ?? provider;

export const getPrimaryAICoAuthorDisplayName = (aiCoAuthorProviders?: AICodeAuthorProvider[]): string | null => {
    if (aiCoAuthorProviders?.length) {
        return displayNameForAICodeAuthorProvider(aiCoAuthorProviders[0]);
    }
    return null;
};

export const getPrimaryAICodeAuthorProvider = (
    aiCoAuthorProviders?: AICodeAuthorProvider[],
): AICodeAuthorProvider | null => aiCoAuthorProviders?.[0] ?? null;

export const formatCodeOwnerWithAICoAuthor = (codeOwner: string, aiCoAuthorDisplayName: string): string => {
    if (!codeOwner) {
        return aiCoAuthorDisplayName;
    }
    return `${codeOwner} and ${aiCoAuthorDisplayName}`;
};

export const formatPercentageAICoAuthored = (percentageAICoAuthored: number): string => {
    const pct =
        percentageAICoAuthored <= 1 ? Math.round(percentageAICoAuthored * 100) : Math.round(percentageAICoAuthored);
    return `${pct}% AI co-authored`;
};

export const formatAICoAuthorTooltipContent = (
    aiCoAuthors?: string[],
    percentageAICoAuthored?: number,
    includePercentage = false,
): React.ReactNode => {
    const hasNames = !!aiCoAuthors?.length;
    const hasPercentage = includePercentage && percentageAICoAuthored != null && !Number.isNaN(percentageAICoAuthored);

    if (!hasNames && !hasPercentage) {
        return null;
    }

    return (
        <div>
            {aiCoAuthors?.map((name) => <div key={name}>{name}</div>)}
            {hasPercentage && <div>{formatPercentageAICoAuthored(percentageAICoAuthored)}</div>}
        </div>
    );
};

const CODE_OWNER_AVATAR_SIZE = 20;
const CODE_OWNER_AVATAR_OFFSET = 12;

interface AICodeAuthorProviderIconProps {
    provider: AICodeAuthorProvider;
    size?: number;
}

export const AICodeAuthorProviderIcon: React.FC<AICodeAuthorProviderIconProps> = ({
    provider,
    size = CODE_OWNER_AVATAR_SIZE,
}) => (
    <img
        src={providerAvatarSrc[provider]}
        width={size}
        height={size}
        alt=""
        style={{
            borderRadius: '50%',
            flexShrink: 0,
            display: 'block',
        }}
    />
);

interface CodeOwnerAvatarProps {
    codeOwner: string;
    aiProvider?: AICodeAuthorProvider | null;
}

export const CodeOwnerAvatar: React.FC<CodeOwnerAvatarProps> = ({ codeOwner, aiProvider }) => {
    const baseAvatarStyle: React.CSSProperties = {
        fontSize: 10,
    };

    if (aiProvider && !codeOwner) {
        return <AICodeAuthorProviderIcon provider={aiProvider} size={CODE_OWNER_AVATAR_SIZE} />;
    }

    if (!aiProvider) {
        return (
            <Avatar
                style={{
                    ...baseAvatarStyle,
                    backgroundColor: getAvatarColorForName(codeOwner),
                    flexShrink: 0,
                }}
                size={CODE_OWNER_AVATAR_SIZE}
            >
                {codeOwner.charAt(0).toUpperCase()}
            </Avatar>
        );
    }

    return (
        <div
            style={{
                position: 'relative',
                width: CODE_OWNER_AVATAR_SIZE + CODE_OWNER_AVATAR_OFFSET,
                height: CODE_OWNER_AVATAR_SIZE + CODE_OWNER_AVATAR_OFFSET,
                flexShrink: 0,
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    left: CODE_OWNER_AVATAR_OFFSET,
                    bottom: 0,
                    zIndex: 0,
                }}
            >
                <AICodeAuthorProviderIcon provider={aiProvider} size={CODE_OWNER_AVATAR_SIZE} />
            </div>
            <Avatar
                style={{
                    ...baseAvatarStyle,
                    backgroundColor: getAvatarColorForName(codeOwner),
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    zIndex: 1,
                }}
                size={CODE_OWNER_AVATAR_SIZE}
            >
                {codeOwner.charAt(0).toUpperCase()}
            </Avatar>
        </div>
    );
};
